'use client';

import { useQuery } from '@tanstack/react-query';
import { Box } from '@terra/ui-styled-system';
import { AlertCircleIcon, GitBranchIcon } from '@terra/ui-icons';
import { ReactFlowProvider } from '@xyflow/react';
import type { CodeGraphNodeProps } from '@terra/ui-charts';
import { CodeGraph } from '@terra/ui-charts';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useAppTargetId } from '../../store/projectStore';
import { useBottomPanel } from '../../store/layoutStore';
import type { CodeGraphWithRelations } from '@terra/shared-schemas-git';
import { EmptyState } from '../EmptyState';
import { useRepositoryInfoMap } from '../../hooks/useRepositoryInfoMap';

interface CodeGraphContentProps {
  endpointId?: string;
  selectedEndpoint?: {
    method: string;
    path: string;
    domain: string;
  };
}
// Fetch code graph for an endpoint
const getEndpointCodeGraph = async (
  applicationId: string,
  endpointId: string,
): Promise<CodeGraphWithRelations | null> => {
  const result = await terraProxyApiClient.getEndpointCodeGraph(
    applicationId,
    endpointId,
  );

  // The tenantAwareApiWrapper handles status codes and only returns success response body
  // For 200 status, it returns the response body which has the codeGraph property
  return result?.codeGraph || null;
};

export function CodeGraphContent({ endpointId }: CodeGraphContentProps) {
  const appTargetId = useAppTargetId();
  const { setSelectedFileId } = useBottomPanel();
  // Handle node click to select corresponding file
  const handleNodeClick = (node: CodeGraphNodeProps) => {
    // Find the corresponding file in the code graph
    if (codeGraph?.nodes) {
      const nodeData = codeGraph.nodes.find((n) => n.id === node?.id);

      if (nodeData?.filePath) {
        // Set the selected file ID based on the file path
        // We'll use the file path as the identifier for now
        setSelectedFileId(nodeData.filePath);
      }
    }
  };

  // Fetch code graph data
  const {
    data: codeGraph,
    isLoading: isLoadingGraph,
    error: graphError,
  } = useQuery({
    queryKey: ['code-graph', appTargetId, endpointId],
    queryFn: async () => {
      if (!appTargetId || !endpointId) return null;

      return getEndpointCodeGraph(appTargetId, endpointId);
    },
    enabled: !!appTargetId && !!endpointId,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });
  const repositoryInfoMap = useRepositoryInfoMap({
    applicationId: appTargetId,
  });

  if (!endpointId) {
    return (
      <EmptyState
        icon={GitBranchIcon}
        title="No Endpoint Selected"
        description="Select an endpoint to view its code graph."
      />
    );
  }

  if (isLoadingGraph) {
    return (
      <EmptyState
        icon={GitBranchIcon}
        title="Loading code graph..."
        isLoading={true}
      />
    );
  }

  if (graphError || !codeGraph) {
    return (
      <EmptyState
        icon={AlertCircleIcon}
        title="No Code Graph Available"
        description="Code graph generation may be in progress or not yet triggered for this endpoint."
      />
    );
  }

  return (
    <Box h="full" w="full" bg="bg.default" overflow="hidden">
      {codeGraph ? (
        <Box h="full" w="full" minH="400px" minW="600px">
          <ReactFlowProvider>
            <CodeGraph
              data={codeGraph}
              repositoryInfoMap={repositoryInfoMap}
              onNodeClick={handleNodeClick}
            />
          </ReactFlowProvider>
        </Box>
      ) : (
        <EmptyState
          icon={GitBranchIcon}
          title="No Code Graph Available"
          description="Code graph generation may be in progress."
        />
      )}
    </Box>
  );
}
